import React, { useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import moment from 'moment';
import { Theme } from '@app/theme';
import { Popover } from '@mui/material';
import { GenericButton, useBoolModalState } from '@app/core';
import { EventSourceInput } from '@fullcalendar/core';

import { BaseEvent } from './Calendar';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { EventEditPopover } from './EventEditPopover';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    padding: theme.marginBase * 2,
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    display: 'block',
  },
  button: {
    ...theme.fonts.caption,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,

    height: theme.height.small,
    paddingRight: theme.marginBase * 2,
    paddingLeft: theme.marginBase * 2,
    color: theme.colors.inkSecondary,
    minWidth: 120,
    textAlign: 'left',

    '&:hover, &:active, &:focus': {
      backgroundColor: theme.colors.grayBackground,
    },
  },
  icon: {
    fontSize: theme.iconSize.regular,
  },
}));

export interface PopoverInfo {
  coords: {
    x: number;
    y: number;
  };
  eventId: string;
}

interface Props {
  info?: PopoverInfo;
  eventSources?: EventSourceInput[];
  onRequestClose: () => any;
  onEditEvent?: (event: BaseEvent) => any;
  onDeleteEvent?: (event: BaseEvent) => any;
  timezone: string;
  minDuration: number;
  fixedDuration?: boolean;
}

export const EventPopover = ({
  info,
  eventSources,
  onRequestClose,
  onEditEvent,
  onDeleteEvent,
  timezone,
  minDuration,
  fixedDuration,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [editingOpen, openEdit, closeEdit] = useBoolModalState();

  const event = useMemo(() => {
    if (!info || !eventSources) {
      return;
    }
    for (const source of eventSources) {
      if (typeof source === 'function' || typeof source === 'string') {
        continue;
      }
      let events;
      if (Array.isArray(source)) {
        events = source;
      } else if (source.events && Array.isArray(source.events)) {
        events = source.events;
      }
      if (!events) {
        continue;
      }

      const event = events.find((e) => e.id === info.eventId);
      if (event && event.start && event.end) {
        return {
          id: event.id,
          start: moment(event.start).toDate(),
          end: moment(event.end).toDate(),
          ...event,
        } as BaseEvent;
      }
    }
    return;
  }, [eventSources, info]);

  const onDeleteClick = useCallback(() => {
    if (!onDeleteEvent || !event) {
      return;
    }
    onDeleteEvent(event);
  }, [onDeleteEvent, event]);

  const onClose = useCallback(() => {
    onRequestClose();
    closeEdit();
  }, [onRequestClose, closeEdit]);

  if (!info || !onEditEvent || !event) {
    return null;
  }

  return (
    <Popover
      open={!!info}
      anchorReference="anchorPosition"
      anchorPosition={{ top: info.coords.y, left: info.coords.x }}
      onClose={onClose}
    >
      {editingOpen ? (
        <EventEditPopover
          event={event}
          timezone={timezone}
          minDuration={minDuration}
          onEditEvent={onEditEvent}
          fixedDuration={fixedDuration}
        />
      ) : (
        <menu className={classes.menu}>
          <li className={classes.item}>
            <GenericButton className={classes.button} onClick={openEdit}>
              <i className={classNames('ri-edit-line', classes.icon)} />
              {t('calendar.popover.edit')}
            </GenericButton>
          </li>
          <li className={classes.item}>
            <GenericButton className={classes.button} onClick={onDeleteClick}>
              <i className={classNames('ri-delete-bin-line', classes.icon)} />
              {t('calendar.popover.delete')}
            </GenericButton>
          </li>
        </menu>
      )}
    </Popover>
  );
};
